import { z } from 'zod';

/** API input contracts for the CRM kernel. Shared by core-api (validation) and seed scripts. */
const name = z.string().trim().min(1).max(200);
const optText = (max = 500) => z.string().trim().max(max).nullable().optional();
const uuid = z.uuid();

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const CASE_STATUSES = ['new', 'open', 'pending', 'resolved', 'closed'] as const;
export const LIFECYCLE = ['lead', 'contact', 'customer', 'churned'] as const;
export const RELATED_TYPES = ['person', 'account', 'deal', 'case', 'conversation'] as const;

export const PersonInput = z.object({
  displayName: name,
  nameLatin: optText(200),
  nameArabic: optText(200),
  jobTitle: optText(200),
  lifecycleStage: z.enum(LIFECYCLE).optional(),
  city: optText(100),
  country: z.string().length(2).nullable().optional(),
  ownerUserId: uuid.nullable().optional(),
  tags: z.array(z.string().max(40)).max(20).optional(),
  phones: z.array(z.string().max(40)).max(5).optional(),
  emails: z.array(z.email()).max(5).optional(),
  accountId: uuid.optional(),
  role: optText(100),
});
export const PersonPatch = PersonInput.omit({
  phones: true,
  emails: true,
  accountId: true,
  role: true,
}).partial();

export const AccountInput = z.object({
  name,
  nameAr: optText(200),
  industry: optText(100),
  website: optText(300),
  phone: optText(40),
  email: z.email().nullable().optional(),
  city: optText(100),
  country: z.string().length(2).nullable().optional(),
  vatNumber: optText(40),
  ownerUserId: uuid.nullable().optional(),
  tags: z.array(z.string().max(40)).max(20).optional(),
});
export const AccountPatch = AccountInput.partial().extend({
  status: z.enum(['active', 'archived']).optional(),
});

export const RelationshipInput = z.object({
  personId: uuid,
  accountId: uuid,
  role: optText(100),
  isPrimary: z.boolean().optional(),
});

/*
 * Update schemas are defined from field sets WITHOUT defaults. `.partial()` keeps zod defaults, so deriving a patch
 * schema from a create schema would silently reset omitted fields (e.g. amount to 0) on every update.
 */
const DealFields = z.object({
  title: name,
  amount: z.number().nonnegative().max(1e12),
  currency: z.string().length(3),
  stageId: uuid,
  accountId: uuid.nullable(),
  personId: uuid.nullable(),
  ownerUserId: uuid.nullable(),
  expectedCloseDate: z.iso.date().nullable(),
  lostReason: optText(500),
});
export const DealInput = DealFields.omit({ lostReason: true })
  .partial()
  .extend({ title: name, amount: z.number().nonnegative().max(1e12).default(0) });
export const DealPatch = DealFields.partial();

const CaseFields = z.object({
  subject: name,
  description: optText(5000),
  priority: z.enum(PRIORITIES),
  category: optText(100),
  personId: uuid.nullable(),
  accountId: uuid.nullable(),
  conversationId: uuid.nullable(),
  assigneeUserId: uuid.nullable(),
  regulatorFlag: z.boolean(),
  dueAt: z.iso.datetime({ offset: true }).nullable(),
  status: z.enum(CASE_STATUSES),
});
export const CaseInput = CaseFields.omit({ status: true })
  .partial()
  .extend({
    subject: name,
    priority: z.enum(PRIORITIES).default('normal'),
    regulatorFlag: z.boolean().default(false),
  });
export const CasePatch = CaseFields.partial();

const TaskFields = z.object({
  title: name,
  dueAt: z.iso.datetime({ offset: true }).nullable(),
  priority: z.enum(PRIORITIES),
  assigneeUserId: uuid.nullable(),
  relatedType: z.enum(RELATED_TYPES).nullable(),
  relatedId: uuid.nullable(),
  status: z.enum(['open', 'done', 'cancelled']),
});
export const TaskInput = TaskFields.omit({ status: true })
  .partial()
  .extend({ title: name, priority: z.enum(PRIORITIES).default('normal') });
export const TaskPatch = TaskFields.partial();

export const NoteInput = z.object({
  body: z.string().trim().min(1).max(10_000),
  relatedType: z.enum(RELATED_TYPES),
  relatedId: uuid,
});

/** Money: amounts arrive in major units and are stored as integer minor units (3 decimals for KWD/BHD/OMR). */
export const MINOR_EXPONENT: Record<string, number> = { KWD: 3, BHD: 3, OMR: 3, JOD: 3 };
export const toMinor = (amount: number, currency: string) =>
  Math.round(amount * 10 ** (MINOR_EXPONENT[currency] ?? 2));
export const fromMinor = (minor: number | string, currency: string) =>
  Number(minor) / 10 ** (MINOR_EXPONENT[currency] ?? 2);
